import type { DeliveryLabelLine } from './delivery-labels.js';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Loading,
  Modal,
  Mono,
  Screen,
  SectionNav,
} from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { useQuietUntilTomorrow } from '../../lib/quiet-until-tomorrow.js';
import { statusLabel } from '../../lib/status-label.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import { DateInput } from '../../components/date-input.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import { FinishFromOrder } from '../products/finish-products.js';
import { InvoiceScreen } from './invoice-screen.js';
import {
  LooseReceipts,
  LooseReceivingBench,
  StartWithoutOrderDialog,
} from './receiving-without-order.js';
import { useReceiptPrinting } from '../sales/receipt.js';
import { DeliveryReceived } from './delivery-received.js';
import { NewOrderScreen } from './new-order-screen.js';
import { usePurchaseOrderPrinter } from './purchase-order-document.js';
import {
  useCumulativeReceivingPrinter,
  useReceivingDocumentPrinter,
} from './receiving-document.js';
import {
  isFullyCounted,
  isLineSettled,
  parseCount,
  receivableUnits,
  receivingStyles,
  toggleAllArrived,
} from './receiving-lines.js';
import type { ReceivingLineCounts } from './receiving-lines.js';
import { VendorReturnScreen, VendorReturnsCard } from './vendor-returns.js';
import './receiving.css';

/**
 * Purchase orders and receiving (SCOPE §6, §7).
 *
 * The back half of the flagship workflow. Import builds the order, this puts it
 * on the shelf and prints the labels.
 *
 * Step budget (§2.2): receiving a complete delivery is four interactions —
 * Receive, It all arrived, Commit, Print labels. The list goes straight to the
 * bench rather than through a preview dialog, because the row already carries
 * everything needed to pick the right order.
 */

const STATUS_TONE = {
  draft: 'neutral',
  submitted: 'info',
  partially_received: 'warning',
  received: 'success',
  reconciled: 'success',
  cancelled: 'danger',
} as const;

function formatDate(value: string | null): string {
  if (value === null) return '—';
  return new Date(value).toLocaleDateString();
}

/** Whole days since a date, for the ageing figures (item 35). */
function daysSince(value: string | null): number | null {
  if (value === null) return null;
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
  return days < 0 ? null : days;
}

/** Orders shown at a time; "Show more" adds as many again. */
const PAGE = 30;

type OrderState = 'all' | 'open' | 'received' | 'cancelled';

export function PurchaseOrders({
  nav,
  canPurchase = false,
  canReconcile = false,
  initialExpanded = null,
  onSeeReorder,
}: {
  nav?: import('react').ReactNode;
  /** Writing an order is a buying decision; receiving one is stockroom work. */
  canPurchase?: boolean;
  /** Posting a return to vendor takes stock out and settles with the vendor. */
  canReconcile?: boolean;
  /** Arrive with one order already open — how the import's "Open PO-…" lands. */
  initialExpanded?: string | null;
  /** Opens Reports → Reorder. Absent for somebody who may not read reports. */
  onSeeReorder?: () => void;
} = {}) {
  const queryClient = useQueryClient();
  const [receivingId, setReceivingId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [invoicing, setInvoicing] = useState<string | null>(null);
  /** Receiving with no purchase order (SCOPE §6.1): choosing the vendor, then counting. */
  const [startingLoose, setStartingLoose] = useState(false);
  const [looseId, setLooseId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(initialExpanded);
  const [cancelling, setCancelling] = useState<{ id: string; number: string } | null>(null);
  /** A return to vendor open full-screen; id null while a new one is written (decision 181). */
  const [returning, setReturning] = useState<{ id: string | null } | null>(null);
  /** Orders, or the returns to vendor — each its own list, a press apart (2026-10-02). */
  const [view, setView] = useState<'orders' | 'returns'>('orders');
  /** Find an order by number, vendor or their reference — or by scanning the printed PO. */
  const [term, setTerm] = useState('');
  /** Typing anywhere on the list types into Find an order (client, 2026-10-15). */
  const searchRef = useSearchTyping(setTerm, view === 'orders');
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState<OrderState>('all');
  const [limit, setLimit] = useState(PAGE);
  const [findProblem, setFindProblem] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 200);
    return () => clearTimeout(timer);
  }, [term]);

  const settings = useReceiptPrinting();
  const receivingDoc = useReceivingDocumentPrinter(settings);
  const cumulativeDoc = useCumulativeReceivingPrinter(settings);
  const orderDoc = usePurchaseOrderPrinter();

  const orders = useQuery({
    queryKey: ['purchasing', 'orders', debounced, state, limit],
    queryFn: () =>
      api.purchasing.purchaseOrders.query({
        limit,
        ...(debounced === '' ? {} : { term: debounced }),
        ...(state === 'all' ? {} : { state }),
      }),
    placeholderData: keepPreviousData,
    // A purchase order that was just imported must be here, not in fifteen
    // seconds. This list is small and cheap.
    staleTime: 0,
  });

  /** Open returns, counted on the switch so they are seen from the top of the list. */
  const openReturns = useQuery({
    queryKey: ['purchasing', 'vendorReturns', false],
    queryFn: () => api.purchasing.vendorReturns.query({ includeDeleted: false }),
    enabled: canPurchase,
    staleTime: 0,
  });
  const openReturnCount = (openReturns.data ?? []).filter((row) => row.status === 'open').length;

  /**
   * Enter, or a scanner's Enter: the order whose number is exactly what was
   * typed opens — the printed PO carries its number as a barcode. Asked of the
   * server now, not of a list that may still be catching up with the keystrokes.
   */
  const findExact = async () => {
    const wanted = term.trim();
    if (wanted === '') return;
    setDebounced(wanted);
    try {
      const found = await api.purchasing.purchaseOrders.query({ term: wanted, limit: 5 });
      const exact =
        found.find((order) => order.number.toLowerCase() === wanted.toLowerCase()) ??
        (found.length === 1 ? found[0] : undefined);
      if (exact !== undefined) {
        setState('all');
        setExpanded(exact.id);
        setFindProblem(null);
      } else {
        setFindProblem(found.length === 0 ? `No order matches ${wanted}.` : null);
      }
    } catch (error) {
      setFindProblem(errorMessage(error));
    }
  };

  const start = useMutation({
    mutationFn: (purchaseOrderId: string) =>
      api.purchasing.startReceiving.mutate({ purchaseOrderId }),
    onSuccess: (receivingOrderId) => {
      recordBreadcrumb('purchasing', 'started receiving');
      setReceivingId(receivingOrderId);
    },
  });

  /*
   * No reconcile button any more (item 33): the bench closes the order when
   * the last unit arrives, and a second button asserting what the numbers
   * already say was ceremony.
   */

  // Pressing Purchase orders while inside an order — the writing screen, the
  // bench, an expanded row — lands back on the list (item 31, the client's
  // own example). The bench's counts live on the server, so closing it loses
  // nothing.
  useNavReset(() => {
    setWriting(false);
    setInvoicing(null);
    setStartingLoose(false);
    setLooseId(null);
    setReceivingId(null);
    setExpanded(null);
    setCancelling(null);
    setReturning(null);
    setView('orders');
  });

  if (receivingId !== null) {
    return (
      <ReceivingBench
        receivingOrderId={receivingId}
        onDone={() => {
          setReceivingId(null);
          void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
        }}
      />
    );
  }

  if (looseId !== null) {
    return (
      <LooseReceivingBench
        receivingOrderId={looseId}
        canCreateStyles={canPurchase}
        onDone={() => {
          setLooseId(null);
          void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
        }}
      />
    );
  }

  if (returning !== null) {
    return (
      <VendorReturnScreen
        vendorReturnId={returning.id}
        canPost={canReconcile}
        onClose={() => {
          setReturning(null);
          // Back to the list it belongs to, where it now shows.
          setView('returns');
          void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
        }}
      />
    );
  }

  if (invoicing !== null) {
    return (
      <InvoiceScreen
        purchaseOrderId={invoicing}
        onDone={() => {
          setExpanded(invoicing);
          setInvoicing(null);
        }}
      />
    );
  }

  // A task with its own workspace takes over the section rather than floating
  // above it — the same call the style grid made (§2.2-4).
  if (writing) {
    return (
      <NewOrderScreen
        onClose={() => setWriting(false)}
        onCreated={(purchaseOrderId) => {
          // Creating an order opens it (item 29): back to the list with the
          // new order's row expanded — its paperwork, its figures, its Send
          // button — rather than a list where it is one row among thirty.
          setWriting(false);
          setExpanded(purchaseOrderId);
        }}
      />
    );
  }

  return (
    <Screen
      title="Purchase orders"
      subtitle="Receive a delivery, or check what is outstanding."
      primaryAction={
        canPurchase ? (
          <Button variant="primary" onClick={() => setWriting(true)}>
            New order
          </Button>
        ) : undefined
      }
      secondaryActions={
        <>
          {/* The negative of a receipt, beside it (decision 181). */}
          {canPurchase ? (
            <Button variant="secondary" onClick={() => setReturning({ id: null })}>
              Return to vendor
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setStartingLoose(true)}>
            Receive without an order
          </Button>
        </>
      }
    >
      {nav}
      {canPurchase ? (
        <SectionNav
          label="Purchase orders or returns"
          current={view}
          items={[
            { key: 'orders', label: 'Orders' },
            {
              key: 'returns',
              label:
                openReturnCount > 0
                  ? `Returns to vendor · ${openReturnCount} open`
                  : 'Returns to vendor',
            },
          ]}
          onChange={setView}
        />
      ) : null}
      {startingLoose ? (
        <StartWithoutOrderDialog
          onClose={() => setStartingLoose(false)}
          onStarted={(id) => {
            setStartingLoose(false);
            setLooseId(id);
          }}
        />
      ) : null}
      {receivingDoc.frame}
      {cumulativeDoc.frame}
      {orderDoc.frame}
      {orderDoc.error !== null ? (
        <Callout tone="danger">{errorMessage(orderDoc.error)}</Callout>
      ) : null}
      {orders.isError ? <Callout tone="danger">{errorMessage(orders.error)}</Callout> : null}
      {start.isError ? <Callout tone="danger">{errorMessage(start.error)}</Callout> : null}
      {receivingDoc.error !== null ? (
        <Callout tone="danger">{errorMessage(receivingDoc.error)}</Callout>
      ) : null}
      {cumulativeDoc.error !== null ? (
        <Callout tone="danger">{errorMessage(cumulativeDoc.error)}</Callout>
      ) : null}

      {view === 'returns' && canPurchase ? (
        <VendorReturnsCard onOpen={(id) => setReturning({ id })} />
      ) : (
        <>
          <ReorderBanner {...(onSeeReorder === undefined ? {} : { onSee: onSeeReorder })} />
          <div className="po-find">
            <Field
              label="Find an order"
              hint="PO number, vendor or their order number — or scan the barcode on a printed PO."
            >
              {(props) => (
                <input
                  {...props}
                  ref={searchRef}
                  className="input"
                  placeholder="PO-1042, Burton, or scan"
                  value={term}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    setTerm(event.target.value);
                    setFindProblem(null);
                    setLimit(PAGE);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void findExact();
                    } else if (event.key === 'Escape') {
                      setTerm('');
                    }
                  }}
                />
              )}
            </Field>
            <Field label="Show">
              {(props) => (
                <select
                  {...props}
                  className="select"
                  value={state}
                  onChange={(event) => {
                    setState(event.target.value as OrderState);
                    setLimit(PAGE);
                  }}
                >
                  <option value="all">Every order</option>
                  <option value="open">Still expected</option>
                  <option value="received">Received</option>
                  <option value="cancelled">Canceled</option>
                </select>
              )}
            </Field>
          </div>
          {findProblem !== null ? <Callout tone="warning">{findProblem}</Callout> : null}

          <Card padded={false}>
            {orders.isPending ? (
              <Loading label="Loading purchase orders" />
            ) : (orders.data?.length ?? 0) === 0 ? (
              <p className="text-secondary receiving__empty">
                {debounced === '' && state === 'all'
                  ? 'No purchase orders yet. Import a vendor file, or write one by hand.'
                  : 'No order matches that.'}
              </p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>PO</th>
                      <th>Vendor</th>
                      <th>Ordered</th>
                      {/* Ageing (item 35): "nothing for 63 days" is what makes
                      somebody pick up the phone. */}
                      <th>Last receipt</th>
                      <th className="num">Units</th>
                      <th className="num">Received</th>
                      <th className="num">Outstanding</th>
                      <th className="num">Cost</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {orders.data?.map((order) => {
                      const settled = order.outstandingUnits === 0;
                      const closed =
                        order.status === 'reconciled' ||
                        order.status === 'cancelled' ||
                        order.status === 'received';
                      const open = !closed && order.outstandingUnits > 0;
                      const orderedDays = daysSince(order.orderedAt);
                      const receiptDays = daysSince(order.lastReceivedAt);
                      const shipOverdue = open ? daysSince(order.shipDate) : null;

                      return (
                        <Fragment key={order.id}>
                          <tr>
                            <td>
                              {/*
                          The number is the affordance for the order's receipts
                          (§6.1). Progressive disclosure rather than a second
                          control in every row: reprinting a receiving document
                          is what happens weeks later when the vendor's invoice
                          turns up, not during a delivery (§2.2-2).
                        */}
                              <button
                                type="button"
                                className="receiving__expand"
                                aria-expanded={expanded === order.id}
                                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                              >
                                <Mono>{order.number}</Mono>
                              </button>{' '}
                              <Badge
                                tone={
                                  STATUS_TONE[order.status as keyof typeof STATUS_TONE] ?? 'neutral'
                                }
                              >
                                {statusLabel(order.status)}
                              </Badge>
                            </td>
                            <td>{order.vendorName}</td>
                            {/* The date is what tells two orders from the same vendor
                          apart at a glance — which is what the preview dialog
                          used to be for. */}
                            <td className="text-secondary">
                              {formatDate(order.orderedAt)}
                              {open && orderedDays !== null && orderedDays > 0 ? (
                                <span className="receiving__age"> · {orderedDays}d</span>
                              ) : null}
                            </td>
                            <td className="text-secondary">
                              {order.lastReceivedAt === null ? (
                                open && orderedDays !== null && orderedDays > 30 ? (
                                  // Old and empty-handed is the row worth a call.
                                  <Badge tone="warning">nothing in {orderedDays}d</Badge>
                                ) : (
                                  '—'
                                )
                              ) : (
                                <>
                                  {formatDate(order.lastReceivedAt)}
                                  {open && receiptDays !== null && receiptDays > 0 ? (
                                    <span className="receiving__age"> · {receiptDays}d</span>
                                  ) : null}
                                </>
                              )}
                              {shipOverdue !== null && shipOverdue > 0 ? (
                                <>
                                  {' '}
                                  <Badge tone="warning">ship date +{shipOverdue}d</Badge>
                                </>
                              ) : null}
                            </td>
                            <td className="num">{order.orderedUnits}</td>
                            <td className="num">{order.receivedUnits}</td>
                            <td className="num">
                              {order.outstandingUnits > 0 ? (
                                <Badge tone="warning">{order.outstandingUnits}</Badge>
                              ) : (
                                <span className="text-secondary">—</span>
                              )}
                            </td>
                            <td className="num money">{money(order.totalCostCents)}</td>
                            <td className="num">
                              {closed || settled ? (
                                <span className="text-secondary">Closed</span>
                              ) : (
                                <Button
                                  variant="primary"
                                  busy={start.isPending && start.variables === order.id}
                                  onClick={() => start.mutate(order.id)}
                                >
                                  Receive
                                </Button>
                              )}
                            </td>
                          </tr>
                          {expanded === order.id ? (
                            <tr>
                              <td colSpan={9}>
                                {/*
                              The order's own paperwork above the deliveries
                              made against it: sending it is what happens first
                              and what happens once, so it should not be found
                              below a list of receipts that may be empty.
                            */}
                                <OrderPaperwork
                                  order={order}
                                  canPurchase={canPurchase}
                                  printer={orderDoc}
                                  onCheckInvoice={() => setInvoicing(order.id)}
                                  onSent={() =>
                                    void queryClient.invalidateQueries({ queryKey: ['purchasing'] })
                                  }
                                />
                                <Receipts
                                  purchaseOrderId={order.id}
                                  printer={receivingDoc}
                                  cumulativePrinter={cumulativeDoc}
                                />
                                {/* What this order's styles still owe (item 39):
                                the moment the boxes are open is the moment
                                bins and prices are decided. */}
                                {canPurchase ? (
                                  <FinishFromOrder purchaseOrderId={order.id} />
                                ) : null}
                                {/* The way out of an order that is never coming
                                (item 35): write off what is outstanding, keep
                                what arrived. Manager-gated on the server. */}
                                {canPurchase && open ? (
                                  <div className="receiving__cancel-order">
                                    <Button
                                      variant="danger"
                                      onClick={() =>
                                        setCancelling({ id: order.id, number: order.number })
                                      }
                                    >
                                      Cancel order
                                    </Button>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {(orders.data?.length ?? 0) >= limit ? (
            <div className="po-more">
              <Button variant="secondary" onClick={() => setLimit((current) => current + PAGE)}>
                Show {PAGE} more
              </Button>
            </div>
          ) : null}

          {/* Deliveries that came with no order: the one being counted, and the last few. */}
          <LooseReceipts onContinue={setLooseId} printer={receivingDoc} />
        </>
      )}

      {cancelling !== null ? (
        <CancelOrderDialog
          order={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
          }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Cancel a whole order (item 35): every outstanding unit written off with a
 * reason, what arrived left intact. The server closes a part-received order
 * as `received` and only an empty one as `cancelled`, and gates the act on
 * the same manager elevation a single backorder cancellation uses.
 */
function CancelOrderDialog({
  order,
  onClose,
  onCancelled,
}: {
  order: { id: string; number: string };
  onClose: () => void;
  onCancelled: () => void;
}) {
  const notify = useNotice();
  const [reason, setReason] = useState('');
  const [approving, setApproving] = useState(false);

  const cancel = useMutation({
    mutationFn: (override?: { employeeId: string; pin: string }) =>
      api.purchasing.cancelPurchaseOrder.mutate({
        purchaseOrderId: order.id,
        reason: reason.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('purchasing', 'canceled a purchase order');
      notify({ title: `${order.number} canceled` });
      onCancelled();
    },
    onError: (error) => {
      if (isOverridable(error)) setApproving(true);
    },
  });

  if (approving) {
    return (
      <ManagerApproval
        title="Cancel this order?"
        because={`Cancel ${order.number} — every outstanding unit is written off: ${reason.trim()}`}
        busy={cancel.isPending}
        error={
          cancel.isError && !isOverridable(cancel.error) ? approvalRefusal(cancel.error) : null
        }
        onCancel={() => setApproving(false)}
        onApprove={(override) => cancel.mutate(override)}
      />
    );
  }

  return (
    <Modal
      open
      tone="danger"
      title={`Cancel ${order.number}?`}
      description="Everything still outstanding is written off. What has already arrived stays received, on the shelf and in the books."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Keep the order
        </Button>
      }
      primaryAction={
        <Button
          variant="danger"
          busy={cancel.isPending}
          disabledReason={
            reason.trim() === '' ? 'Say why — the write-off is the record' : undefined
          }
          onClick={() => cancel.mutate(undefined)}
        >
          Cancel the order
        </Button>
      }
    >
      {cancel.isError && !isOverridable(cancel.error) ? (
        <Callout tone="danger">{errorMessage(cancel.error)}</Callout>
      ) : null}
      <div className="field">
        <label className="field__label" htmlFor="cancel-order-reason">
          Why is it being canceled?
        </label>
        <input
          id="cancel-order-reason"
          className="input"
          placeholder="Vendor sold out for the season"
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * The receiving bench.
 *
 * Scanner-first: the box is open, the scanner is in one hand, and each scan
 * counts one more of that item in. "It all arrived" and the per-line tick are
 * there because most deliveries are complete, and confirming beats typing
 * (§2.2-5, smart defaults).
 */
function ReceivingBench({
  receivingOrderId,
  onDone,
}: {
  receivingOrderId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  /** Grid by default (2026-10-01); the list for an order of one-offs. */
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [committed, setCommitted] = useState<{ number: string; units: number } | null>(null);
  // A scan with the caret anywhere on the delivery lands in its box (2026-10-16).
  const scanRef = useSearchTyping(setScan);

  const order = useQuery({
    queryKey: ['purchasing', 'receiving', receivingOrderId],
    queryFn: () => api.purchasing.receivingOrder.query({ id: receivingOrderId }),
    staleTime: 0,
  });

  const apply = (next: Awaited<ReturnType<typeof api.purchasing.receivingOrder.query>>) => {
    queryClient.setQueryData(['purchasing', 'receiving', receivingOrderId], next);
  };

  const setQuantity = useMutation({
    mutationFn: (input: { variantId: string; quantity: number }) =>
      api.purchasing.setReceivedQuantity.mutate({ receivingOrderId, ...input }),
    onSuccess: apply,
  });

  const receiveAll = useMutation({
    mutationFn: () => api.purchasing.receiveAll.mutate({ receivingOrderId }),
    onSuccess: (next) => {
      apply(next);
      scanRef.current?.focus();
    },
  });

  const commit = useMutation({
    mutationFn: () => api.purchasing.commitReceiving.mutate({ receivingOrderId }),
    onSuccess: (result) => {
      recordBreadcrumb('purchasing', `received ${result.units} units on ${result.number}`);
      setCommitted({ number: result.number, units: result.units });
      void queryClient.invalidateQueries({ queryKey: ['purchasing', 'orders'] });
    },
  });

  useEffect(() => {
    scanRef.current?.focus();
  }, []);

  const data = order.data;
  const lines = data?.lines ?? [];

  const handleScan = (code: string) => {
    const cleaned = code.trim();
    if (cleaned === '') return;

    const line =
      lines.find((entry) => entry.barcode === cleaned) ??
      lines.find((entry) => entry.sku.toUpperCase() === cleaned.toUpperCase());

    setScan('');

    if (line === undefined) {
      // Named, not generic: at a bench with a box open, "not on this order" and
      // "not in the catalog" call for different next steps.
      setScanError(`${cleaned} is not on this purchase order`);
      return;
    }

    setScanError(null);
    setQuantity.mutate({ variantId: line.variantId, quantity: line.receivingNow + 1 });
  };

  // Each size that came in, for the labels grid (FB-1113).
  const labelLines: DeliveryLabelLine[] = lines
    .filter((line) => line.receivingNow > 0)
    .map((line) => ({
      sku: line.sku,
      barcode: line.barcode,
      productName: line.productName,
      colorName: line.colorName,
      sizeLabel: line.sizeLabel,
      altSize: line.altSize,
      received: line.receivingNow,
      retailCents: line.retailCents,
      cardPriceCents: line.cardPriceCents,
    }));

  if (committed !== null) {
    return (
      <DeliveryReceived
        receivingOrderId={receivingOrderId}
        number={committed.number}
        units={committed.units}
        labelLines={labelLines}
        onDone={onDone}
      />
    );
  }

  const counted = data?.totals.units ?? 0;

  return (
    <Screen
      title="Receiving"
      subtitle={
        data === undefined
          ? undefined
          : `${data.receivingOrder.number} · ${data.receivingOrder.vendorName}`
      }
      destructiveAction={
        <Button variant="ghost" onClick={onDone}>
          Leave for now
        </Button>
      }
      secondaryActions={
        <Button
          variant="secondary"
          busy={receiveAll.isPending}
          disabledReason={
            (data?.totals.outstanding ?? 0) === 0 ? 'Nothing left outstanding' : undefined
          }
          onClick={() => receiveAll.mutate()}
        >
          It all arrived
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={commit.isPending}
          disabledReason={counted === 0 ? 'Scan or count something in first' : undefined}
          onClick={() => commit.mutate()}
        >
          Commit · {counted} unit{counted === 1 ? '' : 's'}
        </Button>
      }
    >
      <input
        ref={scanRef}
        className="search__input"
        placeholder="Scan each item as you unpack it…"
        value={scan}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => setScan(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleScan(scan);
          }
        }}
      />

      {scanError !== null ? <Callout tone="warning">{scanError}</Callout> : null}
      {commit.isError ? <Callout tone="danger">{errorMessage(commit.error)}</Callout> : null}
      {setQuantity.isError ? (
        <Callout tone="danger">{errorMessage(setQuantity.error)}</Callout>
      ) : null}

      {/*
        Grid first (2026-10-01): "find the item, then it's got all colors and
        sizes in grid" — and Sugarbush counted length by length. The list is
        one press away for an order of one-offs, where a grid per style is a
        page of one-cell tables.
      */}
      <div className="receiving__view" role="group" aria-label="Show the order as">
        <Button
          variant={view === 'grid' ? 'primary' : 'secondary'}
          aria-pressed={view === 'grid'}
          onClick={() => setView('grid')}
        >
          Grid
        </Button>
        <Button
          variant={view === 'list' ? 'primary' : 'secondary'}
          aria-pressed={view === 'list'}
          onClick={() => setView('list')}
        >
          List
        </Button>
      </div>

      {view === 'grid' ? (
        order.isPending ? (
          <Card>
            <Loading label="Loading the order" />
          </Card>
        ) : (
          receivingStyles(lines).map((style) => (
            <Card
              key={style.productId}
              title={`${style.productName} · ${style.styleNumber}`}
              padded={false}
            >
              {style.blocks.map((block) => (
                <div className="table-scroll" key={block.altSize ?? 'one'}>
                  <table className="table matrix receiving-grid">
                    {block.label !== null ? (
                      <caption className="matrix__caption">{block.label}</caption>
                    ) : null}
                    <thead>
                      <tr>
                        <th />
                        {block.sizes.map((size, index) => (
                          <th key={size ?? `size-${index}`} className="num">
                            {size ?? 'One size'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, rowIndex) => (
                        <tr key={row.key}>
                          <th scope="row" className="matrix__row-head">
                            {row.label}
                          </th>
                          {row.cells.map((line, index) =>
                            line === undefined ? (
                              <td key={index} />
                            ) : (
                              <td
                                key={line.variantId}
                                className={`num${line.receivingNow > 0 ? ' receiving__counted' : ''}`}
                              >
                                {isLineSettled(line) ? (
                                  <>
                                    <Badge tone="success">Complete</Badge>
                                    <div className="receiving-grid__sku">
                                      <Mono>{line.sku}</Mono>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <CountCell
                                      line={line}
                                      label={line.description}
                                      busy={
                                        setQuantity.isPending &&
                                        setQuantity.variables?.variantId === line.variantId
                                      }
                                      onCommit={(quantity) =>
                                        setQuantity.mutate({ variantId: line.variantId, quantity })
                                      }
                                    />
                                    <div className="order__onhand">of {receivableUnits(line)}</div>
                                    {/*
                                      The SKU, small, last in the box (2026-10-02): the number
                                      on the size's label, for matching a tag to its box. Under
                                      the count, so it never pushes the box out of reach.
                                    */}
                                    <div className="receiving-grid__sku">
                                      <Mono>{line.sku}</Mono>
                                    </div>
                                  </>
                                )}
                              </td>
                            ),
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </Card>
          ))
        )
      ) : null}

      {view === 'list' ? (
        <Card padded={false}>
          {order.isPending ? (
            <Loading label="Loading the order" />
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Ordered</th>
                    <th className="num">Already in</th>
                    <th className="num">Counting now</th>
                    <th className="num">Outstanding</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const settled = isLineSettled(line);

                    return (
                      <tr
                        key={line.variantId}
                        className={line.receivingNow > 0 ? 'receiving__counted' : undefined}
                      >
                        <td>
                          {line.description}
                          <div className="text-secondary">
                            <Mono>{line.sku}</Mono>
                          </div>
                        </td>
                        <td className="num">{line.ordered}</td>
                        <td className="num">{line.previouslyReceived}</td>
                        <td className="num">
                          {settled ? (
                            /* Nothing left to receive on this line — an input
                             here invites a count that cannot be applied. */
                            <Badge tone="success">Complete</Badge>
                          ) : (
                            <CountCell
                              line={line}
                              label={line.description}
                              busy={
                                setQuantity.isPending &&
                                setQuantity.variables?.variantId === line.variantId
                              }
                              onCommit={(quantity) =>
                                setQuantity.mutate({ variantId: line.variantId, quantity })
                              }
                            />
                          )}
                        </td>
                        <td className="num">
                          {line.outstanding > 0 ? (
                            <Badge tone="warning">{line.outstanding}</Badge>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>
                        <td className="num money">{money(line.unitCostCents)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {data !== undefined ? (
        <Callout tone="neutral">
          {data.totals.units} unit{data.totals.units === 1 ? '' : 's'} counted,{' '}
          {money(data.totals.costCents)} at cost. {data.totals.outstanding} still outstanding — the
          remainder stays on backorder when you commit.
        </Callout>
      ) : null}
    </Screen>
  );
}

/**
 * One line's count.
 *
 * The input holds its own value and only sends on blur or Enter. Sending on
 * every keystroke re-rendered the table underneath the cursor, which stole
 * focus and threw the page back to the top mid-count.
 *
 * The tick is the common case made cheap: most lines arrive in full, and
 * confirming that is one press instead of typing a number (§2.2-5).
 */
function CountCell({
  line,
  label,
  busy,
  onCommit,
}: {
  line: ReceivingLineCounts;
  label: string;
  busy: boolean;
  onCommit: (quantity: number) => void;
}) {
  const value = line.receivingNow;
  const receivable = receivableUnits(line);
  const [draft, setDraft] = useState(String(value));

  // Follow the server when it changes underneath us — "It all arrived", a
  // scan, or another station.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseCount(draft, value);
    if (parsed !== value) onCommit(parsed);
    else setDraft(String(value));
  };

  const allIn = isFullyCounted(line);

  return (
    <span className="count-cell">
      <input
        className="qty-input num"
        inputMode="numeric"
        aria-label={`Counting ${label}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value.replace(/\D/g, '').slice(0, 4))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            // Leaving the box sends the count, once. Sending here as well sent
            // it twice — and two saves at once made two lines for one item,
            // the extra units committed into stock (2026-10-01).
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
      {/*
        A toggle, not a one-way door: ticking a line by mistake is undone by
        unticking it, without working out what the number used to be (§2.2-11).
      */}
      <button
        type="button"
        className={`count-cell__all${allIn ? ' count-cell__all--on' : ''}`}
        aria-pressed={allIn}
        aria-label={
          allIn ? `Clear the count for ${label}` : `Mark all ${receivable} of ${label} as arrived`
        }
        title={allIn ? 'Clear' : `All ${receivable} arrived`}
        disabled={busy}
        onClick={() => onCommit(toggleAllArrived(line))}
      >
        ✓
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------

/**
 * A purchase order's receipts (SCOPE §6.1, §2-4).
 *
 * One delivery can arrive in several boxes on several days, and each gets its
 * own receiving document. The reason to reprint one is almost always that the
 * vendor's invoice has turned up weeks later and somebody is checking it
 * against what actually arrived (§6.3) — so this is filed under the order, not
 * offered during a delivery.
 */
/**
 * Sending the order, and keeping a copy (SCOPE §6.1).
 *
 * Every purchase order is born `draft` — imported or hand-written — and nothing
 * ever moved it on, so the status was decoration and "have I actually sent
 * this?" had no answer in the system. Sending it is the act of ordering, so
 * emailing it submits it: asking for a second press afterwards is how a sent
 * order stays marked draft forever.
 */
function OrderPaperwork({
  order,
  canPurchase,
  printer,
  onSent,
  onCheckInvoice,
}: {
  order: { id: string; number: string; status: string; vendorName: string };
  canPurchase: boolean;
  printer: ReturnType<typeof usePurchaseOrderPrinter>;
  onSent: () => void;
  /** Opens the invoice check (decision 128). */
  onCheckInvoice: () => void;
}) {
  const notify = useNotice();
  const [emailing, setEmailing] = useState(false);
  const [editing, setEditing] = useState(false);
  const isDraft = order.status === 'draft';

  const submit = useMutation({
    mutationFn: () => api.purchasing.submitPurchaseOrder.mutate({ purchaseOrderId: order.id }),
    onSuccess: () => {
      recordBreadcrumb('purchasing', `marked ${order.number} sent`);
      notify({ title: `${order.number} marked sent to ${order.vendorName}` });
      onSent();
    },
  });

  return (
    <div className="receiving__paperwork">
      <span>
        <Mono>{order.number}</Mono>{' '}
        <span className="text-secondary">
          {isDraft
            ? 'Draft — not sent to the vendor yet.'
            : `Sent to ${order.vendorName}. Emailing again is fine.`}
        </span>
      </span>

      <div className="receiving__paperwork-actions">
        <Button variant="ghost" busy={printer.busy} onClick={() => printer.print(order.id)}>
          Print order
        </Button>
        {canPurchase ? (
          <>
            <Button variant="secondary" onClick={() => setEmailing(true)}>
              Email to vendor
            </Button>
            {/* Dates, terms and notes after it is written (FB-1066, FB-1069). */}
            {order.status === 'cancelled' ? null : (
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
            {/*
              Only while it is a draft, and only as the quieter option — a shop
              that faxes or phones an order still needs to record that it went,
              but emailing it is the path that marks it sent on its own.
            */}
            {isDraft ? (
              <Button variant="ghost" busy={submit.isPending} onClick={() => submit.mutate()}>
                Mark as sent
              </Button>
            ) : null}
            {/*
              An invoice bills an order that went out, so a draft has nothing
              to check yet (§2.2-13: a control that cannot act is not shown).
            */}
            {isDraft ? null : (
              <Button variant="secondary" onClick={onCheckInvoice}>
                Check an invoice
              </Button>
            )}
          </>
        ) : null}
      </div>

      {submit.isError ? <Callout tone="danger">{errorMessage(submit.error)}</Callout> : null}

      {editing ? (
        <OrderDetailsDialog
          purchaseOrderId={order.id}
          number={order.number}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {emailing ? (
        <EmailOrderDialog
          order={order}
          onClose={() => setEmailing(false)}
          onSent={() => {
            setEmailing(false);
            onSent();
          }}
        />
      ) : null}
    </div>
  );
}

/** Send it, with the vendor's address pre-filled and a line to say anything. */
function EmailOrderDialog({
  order,
  onClose,
  onSent,
}: {
  order: { id: string; number: string; vendorName: string };
  onClose: () => void;
  onSent: () => void;
}) {
  const [to, setTo] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [copyToStore, setCopyToStore] = useState(true);
  const [done, setDone] = useState<{ to: string; delivered: boolean } | null>(null);

  const document = useQuery({
    queryKey: ['purchasing', 'purchaseOrderDocument', order.id],
    queryFn: () => api.purchasing.purchaseOrderDocument.query({ purchaseOrderId: order.id }),
  });

  const onFile = document.data?.purchaseOrder.vendorEmail ?? '';
  const address = to ?? onFile;

  const send = useMutation({
    mutationFn: () =>
      api.purchasing.emailPurchaseOrder.mutate({
        purchaseOrderId: order.id,
        to: address.trim() === '' ? undefined : address.trim(),
        note: note.trim() === '' ? undefined : note.trim(),
        copyToStore,
      }),
    onSuccess: (result) => {
      recordBreadcrumb('purchasing', `emailed ${order.number} to ${result.to}`);
      setDone({ to: result.to, delivered: result.delivered });
      onSent();
    },
  });

  if (done !== null) {
    return (
      <Modal
        open
        title={done.delivered ? 'Order sent' : 'Order recorded as sent'}
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <Callout tone={done.delivered ? 'success' : 'warning'}>
          {done.delivered
            ? `${order.number} is on its way to ${done.to}, and the order is marked sent.`
            : `${order.number} is marked sent, but no mail server is configured — nothing actually left the building.`}
        </Callout>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={`Email ${order.number}`}
      description={`The order goes in the body of the message, so ${order.vendorName} can read it without opening an attachment.`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={send.isPending}
          disabledReason={
            document.data === undefined
              ? 'Loading the order'
              : document.data.lines.length === 0
                ? 'There is nothing on this order to send'
                : address.trim() === ''
                  ? 'Enter an address to send to'
                  : undefined
          }
          onClick={() => send.mutate()}
        >
          Send order
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="po-email-to">
          Send to
        </label>
        <input
          id="po-email-to"
          className="input"
          type="email"
          autoFocus
          placeholder="orders@vendor.example"
          value={address}
          onChange={(event) => setTo(event.target.value)}
        />
        <p className="field__hint">
          {onFile === ''
            ? 'This vendor has no address on file, so this one is used just for this order.'
            : `On file for ${order.vendorName}.`}
        </p>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="po-email-note">
          Anything to say? <span className="text-secondary">Optional</span>
        </label>
        <textarea
          id="po-email-note"
          className="input"
          rows={3}
          maxLength={2000}
          placeholder="Ship the smalls first if the run is short."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <label className="field feedback-severity__option">
        <input
          type="checkbox"
          checked={copyToStore}
          onChange={(event) => setCopyToStore(event.target.checked)}
        />
        <span>File a copy with the shop</span>
      </label>

      {document.data !== undefined ? (
        <p className="text-secondary">
          {document.data.totals.lines} lines · {document.data.totals.units} units ·{' '}
          {money(document.data.totals.costCents)}
        </p>
      ) : null}

      {send.isError ? <Callout tone="danger">{errorMessage(send.error)}</Callout> : null}
    </Modal>
  );
}

function Receipts({
  purchaseOrderId,
  printer,
  cumulativePrinter,
}: {
  purchaseOrderId: string;
  printer: ReturnType<typeof useReceivingDocumentPrinter>;
  cumulativePrinter: ReturnType<typeof useCumulativeReceivingPrinter>;
}) {
  const receipts = useQuery({
    queryKey: ['purchasing', 'receivingOrders', purchaseOrderId],
    queryFn: () => api.purchasing.receivingOrders.query({ purchaseOrderId, limit: 50 }),
  });

  if (receipts.isPending) return <Loading label="Loading receipts" />;

  const committed = (receipts.data ?? []).filter((entry) => entry.status === 'committed');

  if (committed.length === 0) {
    return (
      <p className="text-secondary">
        Nothing has been received against this order yet, so there is no document to print.
      </p>
    );
  }

  return (
    <>
      {/*
        The cumulative paper is the default (item 34): one document per
        order, everything received to date, with the history at its foot —
        what actually gets filed against the vendor's invoice.
      */}
      <div className="receiving__cumulative">
        <Button
          variant="secondary"
          busy={cumulativePrinter.busy}
          onClick={() => cumulativePrinter.print(purchaseOrderId)}
        >
          Print receiving document — all received to date
        </Button>
      </div>
      <ul className="receiving__receipts">
        {committed.map((receipt) => (
          <li key={receipt.id}>
            <span>
              <Mono>{receipt.number}</Mono>{' '}
              <span className="text-secondary">
                {receipt.receivedAt === null
                  ? ''
                  : new Date(receipt.receivedAt).toLocaleDateString()}
              </span>
            </span>
            {/* Still reachable per delivery, for checking one packing slip. */}
            <Button variant="ghost" busy={printer.busy} onClick={() => printer.print(receipt.id)}>
              Print this delivery only
            </Button>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The low-stock alert (§6.5, decision 122).
 *
 * A minimum nobody is told about is a number typed for nothing. This is the
 * telling: where a buyer already is when they decide what to order, how many
 * sizes have fallen below their minimum — counting what is already on its way
 * — and across how many vendors, with the list one press away. It reads the
 * same rule the Reorder report groups, so the two cannot disagree.
 *
 * Quiet when there is nothing to say (§2.2-13), and never a gate: an alert
 * that blocks ordering would be absurd. "Not now" quiets it until tomorrow on
 * this till, like the finishing banner, because the shortfall is still real
 * tomorrow — it was the tab's session, which on a till never ends.
 */
function ReorderBanner({ onSee }: { onSee?: () => void }) {
  const [dismissed, dismiss] = useQuietUntilTomorrow('reorder-banner');
  const low = useQuery({
    queryKey: ['purchasing', 'reorderSummary'],
    queryFn: () => api.purchasing.reorderSummary.query(),
  });

  const sizes = low.data?.sizes ?? 0;
  if (dismissed || sizes === 0) return null;
  const vendors = low.data?.vendors ?? 0;

  return (
    <Callout tone="warning">
      <div className="finish-banner">
        <span>
          {sizes} size{sizes === 1 ? ' is' : 's are'} below {sizes === 1 ? 'its' : 'their'} minimum
          {vendors > 0 ? ` across ${vendors} vendor${vendors === 1 ? '' : 's'}` : ''} — counting
          what is already on order.
        </span>
        <span className="finish-banner__actions">
          {onSee === undefined ? null : (
            <Button variant="primary" onClick={onSee}>
              See what to reorder
            </Button>
          )}
          <Button variant="ghost" onClick={dismiss}>
            Not now
          </Button>
        </span>
      </div>
    </Callout>
  );
}

/**
 * An order's paperwork, after it is written: the vendor's number, season,
 * dates, terms, discount and notes. The cancel date was stored and printed as
 * "Cancel after" and nothing could set it (FB-1069); terms and a discount had
 * nowhere to go at all (FB-1066). What is ordered is not edited here — that
 * moves through receiving and backorders, which keep the counts honest.
 */
function OrderDetailsDialog({
  purchaseOrderId,
  number,
  onClose,
}: {
  purchaseOrderId: string;
  number: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const current = useQuery({
    queryKey: ['purchasing', 'purchaseOrderDocument', purchaseOrderId],
    queryFn: () => api.purchasing.purchaseOrderDocument.query({ purchaseOrderId }),
  });
  const [draft, setDraft] = useState<Record<DetailField, string> | null>(null);
  useEffect(() => {
    if (current.data === undefined || draft !== null) return;
    const header = current.data.purchaseOrder;
    setDraft({
      vendorReference: header.vendorReference ?? '',
      season: header.season ?? '',
      shipDate: header.shipDate ?? '',
      cancelDate: header.cancelDate ?? '',
      terms: header.terms ?? '',
      discountTerms: header.discountTerms ?? '',
      notes: header.notes ?? '',
    });
  }, [current.data, draft]);

  const save = useMutation({
    mutationFn: () => {
      const value = (field: DetailField) =>
        draft![field].trim() === '' ? null : draft![field].trim();
      return api.purchasing.updatePurchaseOrderDetails.mutate({
        purchaseOrderId,
        vendorReference: value('vendorReference'),
        season: value('season'),
        shipDate: value('shipDate'),
        cancelDate: value('cancelDate'),
        terms: value('terms'),
        discountTerms: value('discountTerms'),
        notes: value('notes'),
      });
    },
    onSuccess: async () => {
      recordBreadcrumb('purchasing', `edited the details of ${number}`);
      notify({ title: `Saved — ${number}` });
      await queryClient.invalidateQueries({ queryKey: ['purchasing'] });
      onClose();
    },
  });

  const input = (field: DetailField, label: string, type: 'text' | 'date' = 'text') => (
    <div className="field">
      <label className="field__label" htmlFor={`po-${field}`}>
        {label}
      </label>
      {type === 'date' ? (
        <DateInput
          id={`po-${field}`}
          value={draft?.[field] ?? ''}
          onChange={(value) => setDraft((was) => ({ ...was!, [field]: value }))}
        />
      ) : (
        <input
          id={`po-${field}`}
          className="input"
          value={draft?.[field] ?? ''}
          onChange={(event) => setDraft((was) => ({ ...was!, [field]: event.target.value }))}
        />
      )}
    </div>
  );

  return (
    <Modal
      open
      title={`Details of ${number}`}
      onClose={onClose}
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={draft === null ? 'Loading the order' : undefined}
          onClick={() => save.mutate()}
        >
          Save details
        </Button>
      }
    >
      {draft === null ? (
        <Loading label="Loading the order" />
      ) : (
        <div className="style-form">
          {input('vendorReference', 'Vendor’s order number')}
          {input('season', 'Season')}
          {input('shipDate', 'Expected ship date', 'date')}
          {input('cancelDate', 'Cancel after', 'date')}
          {input('terms', 'Payment terms')}
          {input('discountTerms', 'Discount')}
          {input('notes', 'Notes for the vendor')}
        </div>
      )}
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}
    </Modal>
  );
}

type DetailField =
  'vendorReference' | 'season' | 'shipDate' | 'cancelDate' | 'terms' | 'discountTerms' | 'notes';
